import { useColorScheme } from '@mui/material/styles';
import { Monitor, Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type ThemeValue = 'light' | 'dark' | 'system';

const THEME_OPTIONS: Array<{
    value: ThemeValue;
    icon: React.ReactElement;
}> = [
    { value: 'light', icon: <Sun className="h-5 w-5" /> },
    { value: 'dark', icon: <Moon className="h-5 w-5" /> },
    { value: 'system', icon: <Monitor className="h-5 w-5" /> },
];

/**
 * Selector de tema. Escribe en el modo de MUI, que a su vez pone la clase
 * `light`/`dark` en <html> — el mismo interruptor que usa Tailwind.
 */
export function ThemeSetting(): React.ReactElement | null {
    const { t } = useTranslation();
    const { mode, setMode } = useColorScheme();

    // Sin CssVarsProvider (por ejemplo en un test aislado) no hay modo que
    // configurar.
    if (!mode) return null;

    return (
        <section className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
                <h3 className="m-0 text-sm font-medium">
                    {t('settings.theme.label')}
                </h3>
                <p className="text-muted-foreground m-0 text-xs">
                    {t('settings.theme.help')}
                </p>
            </div>
            <div className="grid grid-cols-3 gap-2">
                {THEME_OPTIONS.map((option) => {
                    const isActive = mode === option.value;

                    return (
                        <button
                            key={option.value}
                            type="button"
                            aria-pressed={isActive}
                            onClick={() => setMode(option.value)}
                            className={`flex cursor-pointer flex-col items-center gap-2 rounded-lg border p-4 transition-colors ${
                                isActive
                                    ? 'bg-primary text-primary-foreground border-primary-foreground'
                                    : 'border-border text-foreground hover:bg-accent'
                            }`}
                        >
                            {option.icon}
                            <span className="text-xs font-medium">
                                {t(`settings.theme.${option.value}`)}
                            </span>
                        </button>
                    );
                })}
            </div>
        </section>
    );
}
